import { Link, useLocation } from "@tanstack/react-router";
import { HashIcon, PinIcon } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";

import { ensureLocalApi } from "../../localApi";
import { parsePraxaConnectedWorkSearch } from "../../praxaConnectedWork";
import { useDesktopPraxaChannelManagement } from "../../state/desktopPraxaChannelManagement";
import { useDesktopPraxaConnectedWork } from "../../state/desktopPraxaConnectedWork";
import { useDesktopPraxaResources } from "../../state/desktopPraxaResources";
import { authorizedConnectedWorkItems } from "../praxa/PraxaConnectedWorkPresentation";
import { PraxaConnectorMonochromeLogo } from "../praxa/PraxaConnectorLogo";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "../ui/sidebar";
import {
  PRAXA_SIDEBAR_CHANNEL_FILTER_LABELS,
  PraxaSidebarChannelRowActions,
  PraxaSidebarChannelViewOptions,
} from "./PraxaSidebarChannelMenus";
import {
  DEFAULT_PRAXA_SIDEBAR_CHANNEL_PREFERENCES,
  buildPraxaSidebarChannelRows,
  parsePraxaSidebarChannelPreferences,
  praxaSidebarChannelPreferenceStorageKey,
  projectPraxaSidebarChannelGroups,
  resolvePraxaSidebarChannelEmptyState,
  resolvePraxaSidebarChannelSourceWarnings,
  togglePraxaSidebarChannelPreference,
  type PraxaSidebarChannelPreferences,
  type PraxaSidebarChannelRow,
} from "./PraxaSidebarChannels.logic";
import { resolvePraxaRouteWorkspaceId } from "./PraxaHistoryNavigation";
import { PraxaSidebarSection } from "./PraxaSidebarSection";

export interface PraxaSidebarChannelsProps {
  /**
   * When provided, rows render only from an authoritative snapshot for this
   * workspace. This prevents a previous workspace's channels flashing during a
   * workspace transition.
   */
  readonly workspaceId?: string;
  readonly defaultExpanded?: boolean;
  readonly persistenceKey?: string;
}

function SidebarChannelsState({
  children,
  live = false,
  role,
}: {
  readonly children: ReactNode;
  readonly live?: boolean;
  readonly role?: "alert";
}) {
  return (
    <p
      aria-live={live ? "polite" : undefined}
      className="px-2 py-1.5 text-xs leading-5 text-sidebar-muted-foreground"
      role={role}
    >
      {children}
    </p>
  );
}

function readPreferences(workspaceId: string | null): PraxaSidebarChannelPreferences {
  if (workspaceId === null || typeof window === "undefined") {
    return DEFAULT_PRAXA_SIDEBAR_CHANNEL_PREFERENCES;
  }
  try {
    const raw = window.localStorage.getItem(praxaSidebarChannelPreferenceStorageKey(workspaceId));
    return parsePraxaSidebarChannelPreferences(raw === null ? null : JSON.parse(raw));
  } catch {
    return DEFAULT_PRAXA_SIDEBAR_CHANNEL_PREFERENCES;
  }
}

function persistPreferences(
  workspaceId: string | null,
  preferences: PraxaSidebarChannelPreferences,
): void {
  if (workspaceId === null || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      praxaSidebarChannelPreferenceStorageKey(workspaceId),
      JSON.stringify(preferences),
    );
  } catch {
    // Sidebar presentation preferences remain usable in memory when storage is blocked.
  }
}

export function PraxaSidebarChannels({
  workspaceId,
  defaultExpanded = true,
  persistenceKey = "channels",
}: PraxaSidebarChannelsProps) {
  const location = useLocation();
  const pathname = location.pathname;
  const routeWorkspaceId = workspaceId ?? resolvePraxaRouteWorkspaceId(pathname);
  const { isMobile, setOpenMobile } = useSidebar();
  const resources = useDesktopPraxaResources();
  const activeWorkspaceId = routeWorkspaceId ?? resources.state?.workspaceId ?? null;
  const connectedWork = useDesktopPraxaConnectedWork(activeWorkspaceId ?? "personal", null);
  const management = useDesktopPraxaChannelManagement();
  const [preferences, setPreferencesState] = useState(() => readPreferences(activeWorkspaceId));
  const [archiveTarget, setArchiveTarget] = useState<PraxaSidebarChannelRow | null>(null);

  useEffect(() => {
    setPreferencesState(readPreferences(activeWorkspaceId));
    setArchiveTarget(null);
  }, [activeWorkspaceId]);

  const setPreferences = (
    update: (current: PraxaSidebarChannelPreferences) => PraxaSidebarChannelPreferences,
  ) => {
    setPreferencesState((current) => {
      const next = update(current);
      persistPreferences(activeWorkspaceId, next);
      return next;
    });
  };

  const praxaChannels =
    activeWorkspaceId !== null &&
    resources.state?.workspaceId === activeWorkspaceId &&
    resources.state.workspaceKind === "organization"
      ? resources.state.channels
      : [];
  const managedChannels =
    activeWorkspaceId !== null && management.state?.workspaceId === activeWorkspaceId
      ? management.state.channels
      : [];
  const connectedWorkState =
    activeWorkspaceId !== null && connectedWork.state?.workspaceId === activeWorkspaceId
      ? connectedWork.state
      : null;
  const slackItems =
    connectedWorkState === null
      ? []
      : authorizedConnectedWorkItems(connectedWorkState.items, connectedWorkState.providers).filter(
          (item) => item.provider === "slack" && item.kind === "slack_channel",
        );
  const rows = useMemo(
    () =>
      activeWorkspaceId === null
        ? []
        : buildPraxaSidebarChannelRows({
            managedChannels,
            preferences,
            praxaChannels,
            slackItems,
            workspaceId: activeWorkspaceId,
          }),
    [activeWorkspaceId, managedChannels, praxaChannels, preferences, slackItems],
  );
  const groups = useMemo(
    () => projectPraxaSidebarChannelGroups(rows, preferences),
    [preferences, rows],
  );
  const visibleRowCount = groups.reduce((count, group) => count + group.rows.length, 0);
  const slackProviderStatus =
    connectedWorkState?.providers.find((provider) => provider.provider === "slack")?.status ?? null;
  const channelAvailability = {
    activeWorkspaceId,
    connectedWork: {
      bridgeAvailable: connectedWork.bridge !== undefined,
      error: connectedWork.error !== null,
      loading: connectedWork.loading,
      slackProviderStatus,
      state: connectedWork.state,
      workspaceMismatch: connectedWork.workspaceMismatch,
    },
    resources: {
      bridgeAvailable: resources.bridge !== undefined,
      error: resources.error !== null,
      loading: resources.loading,
      state: resources.state,
    },
    rowCount: rows.length,
  } as const;
  const emptyState = resolvePraxaSidebarChannelEmptyState(channelAvailability);
  const feedbackMessages = [
    ...new Set(
      [
        ...resolvePraxaSidebarChannelSourceWarnings(channelAvailability),
        connectedWork.actionError,
        management.error,
      ].filter((message): message is string => message !== null),
    ),
  ];
  const selectedSlackItemId = parsePraxaConnectedWorkSearch(
    location.search as Record<string, unknown>,
  ).item;

  const toggleHidden = (row: PraxaSidebarChannelRow) => {
    setPreferences((current) => ({
      ...current,
      hiddenChannelKeys: togglePraxaSidebarChannelPreference(current.hiddenChannelKeys, row.key),
    }));
  };
  const togglePinned = (row: PraxaSidebarChannelRow) => {
    if (row.source === "slack") {
      void connectedWork.togglePinned(row.channelId);
      return;
    }
    setPreferences((current) => ({
      ...current,
      pinnedPraxaChannelIds: togglePraxaSidebarChannelPreference(
        current.pinnedPraxaChannelIds,
        row.channelId,
      ),
    }));
  };

  let content: ReactNode;
  if (activeWorkspaceId === null) {
    content = (
      <SidebarChannelsState>
        {emptyState?.message ?? "Channels are unavailable until a Praxa workspace is selected."}
      </SidebarChannelsState>
    );
  } else if (emptyState !== null) {
    content = (
      <SidebarChannelsState live={emptyState.live}>{emptyState.message}</SidebarChannelsState>
    );
  } else if (visibleRowCount === 0) {
    content = (
      <div>
        <SidebarChannelsState>
          No {PRAXA_SIDEBAR_CHANNEL_FILTER_LABELS[preferences.filter].toLocaleLowerCase()} match
          this view.
        </SidebarChannelsState>
        {feedbackMessages.map((message) => (
          <SidebarChannelsState key={message} role="alert">
            {message}
          </SidebarChannelsState>
        ))}
      </div>
    );
  } else {
    content = (
      <div className="grid gap-1">
        {groups.map((group) => (
          <div key={group.key}>
            {group.label ? (
              <p className="px-2 pb-1 pt-1.5 text-[10px] font-medium uppercase tracking-wide text-sidebar-muted-foreground/70">
                {group.label}
              </p>
            ) : null}
            <SidebarMenu aria-label={group.label ? `${group.label} channels` : "Channels"}>
              {group.rows.map((row) => {
                const praxaChannelPath = `/w/${encodeURIComponent(
                  activeWorkspaceId,
                )}/channels/${encodeURIComponent(row.channelId)}`;
                const connectedWorkPath = `/w/${encodeURIComponent(
                  activeWorkspaceId,
                )}/connected-work`;
                const active =
                  row.source === "praxa"
                    ? pathname === praxaChannelPath || pathname.startsWith(`${praxaChannelPath}/`)
                    : (pathname === connectedWorkPath ||
                        pathname.startsWith(`${connectedWorkPath}/`)) &&
                      selectedSlackItemId === row.channelId;

                return (
                  <SidebarMenuItem key={row.key}>
                    <SidebarMenuButton
                      aria-label={
                        row.source === "slack"
                          ? `Open Slack channel ${row.name} in Work`
                          : `Open ${row.name} Praxa AI channel`
                      }
                      className="min-h-8 items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-sidebar-muted-foreground/80 hover:bg-sidebar-row-hover hover:text-sidebar-foreground"
                      isActive={active}
                      onClick={() => {
                        if (isMobile) setOpenMobile(false);
                      }}
                      render={
                        row.source === "slack" ? (
                          <Link
                            params={{ workspaceId: activeWorkspaceId }}
                            search={{ item: row.channelId }}
                            to="/w/$workspaceId/connected-work"
                          />
                        ) : (
                          <Link
                            params={{
                              workspaceId: activeWorkspaceId,
                              channelId: row.channelId,
                            }}
                            to="/w/$workspaceId/channels/$channelId"
                          />
                        )
                      }
                      size="default"
                    >
                      {row.source === "slack" ? (
                        <PraxaConnectorMonochromeLogo
                          className="size-4"
                          name="Slack"
                          slug="slack"
                        />
                      ) : (
                        <HashIcon aria-hidden className="size-4 shrink-0" />
                      )}
                      <span className="min-w-0 flex-1 truncate">{row.name}</span>
                      {row.pinned ? (
                        <PinIcon
                          aria-label="Pinned"
                          className="mr-0.5 size-3 shrink-0 text-sidebar-muted-foreground/65"
                        />
                      ) : null}
                    </SidebarMenuButton>
                    <PraxaSidebarChannelRowActions
                      onArchive={() => setArchiveTarget(row)}
                      onHide={() => toggleHidden(row)}
                      onOpenSource={() => {
                        if (row.sourceUrl) {
                          void ensureLocalApi()
                            .shell.openExternal(row.sourceUrl)
                            .catch(() => {});
                        }
                      }}
                      onPin={() => togglePinned(row)}
                      onRestore={() =>
                        void management.updateChannel({
                          workspaceId: activeWorkspaceId,
                          channelId: row.channelId,
                          archived: false,
                        })
                      }
                      pinning={connectedWork.pinningItemId === row.channelId}
                      row={row}
                      working={management.working}
                    />
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </div>
        ))}
        {feedbackMessages.map((message) => (
          <SidebarChannelsState key={message} role="alert">
            {message}
          </SidebarChannelsState>
        ))}
      </div>
    );
  }

  return (
    <>
      <PraxaSidebarSection
        actions={
          <PraxaSidebarChannelViewOptions
            preferences={preferences}
            setPreferences={setPreferences}
          />
        }
        defaultExpanded={defaultExpanded}
        persistenceKey={persistenceKey}
        title="Channels"
      >
        {content}
      </PraxaSidebarSection>
      <AlertDialog
        onOpenChange={(open) => {
          if (!open) setArchiveTarget(null);
        }}
        open={archiveTarget !== null}
      >
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive #{archiveTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This Praxa AI channel will leave the active list for everyone. Its history remains
              available, and an administrator can restore it later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="ghost" />}>Cancel</AlertDialogClose>
            <Button
              disabled={management.working || archiveTarget === null || activeWorkspaceId === null}
              onClick={() => {
                if (archiveTarget === null || activeWorkspaceId === null) return;
                const channelId = archiveTarget.channelId;
                setArchiveTarget(null);
                void management.updateChannel({
                  workspaceId: activeWorkspaceId,
                  channelId,
                  archived: true,
                });
              }}
              variant="destructive"
            >
              Archive channel
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </>
  );
}
